import { useEffect } from "react";
import { CoreFieldValue, PrimaryCard, SectionHeader } from "@ucl/library";
import { OptInsFormV2 } from "./OptInsFormV2";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import {
  FinancialApplicationFormValidationDisplayBehavior,
  useFinancialApplicationFormSectionValidation,
  FinancialApplicationFormSectionContextType,
} from "@/hooks/useFinancialApplicationFormSectionValidation";
import { FinancialApplicationSection } from "@/types/enums";
import { useFinancialProducts } from "@/hooks/useFinancialProducts";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";
import "./OptIns.scss";

export interface OptInsProps {
  financialApplicationId: string;
  validationBehavior?: FinancialApplicationFormValidationDisplayBehavior;
  onBlur?: (field: CoreFieldValue) => void;
  initiallyCollapsed?: boolean;
  readOnly?: boolean;
  // Configuration for optional opt-in services
  showCdars?: boolean;
  showInsuredCashSweep?: boolean;
  validationContextType: FinancialApplicationFormSectionContextType;
}

export const OptIns = (props: OptInsProps) => {
  const { formValue, refreshFinancialApplication } = useFinancialApplicationForm(
    props.financialApplicationId,
    props.validationContextType
  );

  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);
  const { products } = useFinancialProducts(financialApplicationApiClient);

  // Refresh data when component mounts to ensure we have the latest data
  // This is especially important when navigating from other steps where data might have changed
  useEffect(() => {
    refreshFinancialApplication();
  }, [props.financialApplicationId]);

  // Get active deposit applications with their associated products
  const activeDepositApplications = formValue?.depositApplications?.filter(
    (depositApp) => depositApp.isActive
  );

  // Filter deposit applications by product type, following the same pattern as AccountDetailsForm
  const businessDepositApplications = activeDepositApplications
    ?.map((depositApp) => {
      // Find the product for this deposit application
      const product = products.find((p) => p.id === depositApp.financialProductId);
      return {
        ...depositApp,
        productName: product?.productName,
        isBusinessProduct: product?.isBusinessProduct,
        isIoltaProduct: product?.isIoltaProduct,
        isPersonalProduct: product?.isPersonalProduct,
      };
    })
    .filter((depositApp) => !!depositApp.productName)
    .filter((depositApp) => depositApp.isBusinessProduct || depositApp.isIoltaProduct) || [];

  const personalDepositApplications = activeDepositApplications
    ?.map((depositApp) => {
      // Find the product for this deposit application
      const product = products.find((p) => p.id === depositApp.financialProductId);
      return {
        ...depositApp,
        productName: product?.productName,
        isBusinessProduct: product?.isBusinessProduct,
        isIoltaProduct: product?.isIoltaProduct,
        isPersonalProduct: product?.isPersonalProduct,
      };
    })
    .filter((depositApp) => !!depositApp.productName)
    .filter((depositApp) => depositApp.isPersonalProduct) || [];

  const { getCollectionValidationErrors } = useFinancialApplicationFormSectionValidation(
    props.financialApplicationId,
    FinancialApplicationSection.OptIns,
    props.validationBehavior,
    props.validationContextType
  );

  const depositApplicationErrors = getCollectionValidationErrors("DepositApplications");

  const isLoading = !formValue;

  return (
    <div className="opt-ins">
      <div className="opt-ins__content">
        {!isLoading && (
          <>
            {/* Business Account Opt-Ins */}
            {businessDepositApplications.length > 0 && (
              <section className="opt-ins__section business-account-opt-ins-section">
                <SectionHeader label="Business Account Opt-Ins" />
                <div className="opt-ins__section__content business-account-opt-ins-section__content">
                  {businessDepositApplications.map((depositApp) => (
                    <PrimaryCard key={depositApp.id}>
                      <OptInsFormV2
                        financialApplicationId={props.financialApplicationId}
                        depositApplicationId={depositApp.id}
                        validationErrors={
                          depositApplicationErrors.fieldSchemaCollectionMemberValidationErrors[
                            depositApp.id
                          ] || {}
                        }
                        onBlur={props.onBlur}
                        isInitiallyCollapsed={props.initiallyCollapsed}
                        allowChangingWhetherCollapsed={!props.readOnly}
                        readOnly={props.readOnly}
                        showCdars={props.showCdars}
                        showInsuredCashSweep={props.showInsuredCashSweep}
                      />
                    </PrimaryCard>
                  ))}
                </div>
              </section>
            )}

            {/* Personal Account Opt-Ins */}
            {personalDepositApplications.length > 0 && (
              <section className="opt-ins__section personal-account-opt-ins-section">
                <SectionHeader label="Personal Account Opt-Ins" />
                <div className="opt-ins__section__content personal-account-opt-ins-section__content">
                  {personalDepositApplications.map((depositApp) => (
                    <PrimaryCard key={depositApp.id}>
                      <OptInsFormV2
                        financialApplicationId={props.financialApplicationId}
                        depositApplicationId={depositApp.id}
                        validationErrors={
                          depositApplicationErrors.fieldSchemaCollectionMemberValidationErrors[
                            depositApp.id
                          ] || {}
                        }
                        onBlur={props.onBlur}
                        isInitiallyCollapsed={props.initiallyCollapsed}
                        allowChangingWhetherCollapsed={!props.readOnly}
                        readOnly={props.readOnly}
                        showCdars={props.showCdars}
                        showInsuredCashSweep={props.showInsuredCashSweep}
                      />
                    </PrimaryCard>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};
